import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { API } from '../utils/helpers';
import MarkdownRenderer from '../components/MarkdownRenderer';
import NatalChartWheel from '../components/NatalChartWheel';
import AspectsTable from '../components/AspectsTable';
import SynastryWheel from '../components/SynastryWheel';

export default function SharedPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const loadShared = async () => {
      try { const res = await fetch(`${API}/shared/${id}`); if (res.ok) setData(await res.json()); else setError(true); } catch (e) { setError(true); }
      setLoading(false);
    };
    loadShared();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  if (error || !data) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 text-center">
      <h1 className="text-2xl font-bold text-purple-400 mb-4">Lectura no encontrada</h1>
      <p className="text-[var(--text-muted)] mb-6">Este enlace puede haber expirado o no ser valido.</p>
      <Link to="/" className="btn-gold">Visitar Madame Blavatsky</Link>
    </div>
  );

  const c = data.content;
  const shareType = data.type || c?.type;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/"><img src="https://customer-assets.emergentagent.com/job_mystic-cards-56/artifacts/wfhpmaw5_Madame-Blavatsky--Logo-2_20260216_193202_0000.png" alt="Madame Blavatsky" className="w-16 h-16 mx-auto mb-3" /></Link>
          <h1 className="font-decorative text-xl text-purple-300" data-testid="shared-title">Madame Blavatsky</h1>
        </div>
        {shareType === 'tarot' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <h2 className="text-xl font-bold text-purple-300 mb-2 text-center" data-testid="shared-tarot-title">{c.spread_type}</h2>
            {c.cards?.length > 0 && (
              <div className="flex justify-center gap-3 flex-wrap my-6">
                {c.cards.map((card, i) => (
                  <div key={i} className="text-center">
                    <div className="w-20 sm:w-24 aspect-[2/3] rounded-lg overflow-hidden border border-gold-300/10 shadow-lg shadow-purple-500/10 mb-1"><img src={card.image} alt={card.name} className="w-full h-full object-cover" /></div>
                    <p className="text-purple-300 text-xs font-medium">{card.name}</p>
                    {card.position && <p className="text-[var(--text-muted)] text-xs">{card.position}</p>}
                  </div>
                ))}
              </div>
            )}
            {c.interpretation && <MarkdownRenderer content={c.interpretation} className="text-[var(--text-secondary)] text-sm" />}
          </motion.div>
        )}
        {shareType === 'horoscope' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <div className="text-center mb-4">
              {c.image && <img src={c.image} alt={c.sign} className="w-16 h-16 mx-auto mb-3 bg-white/90 rounded-full p-1.5" />}
              <h2 className="text-xl font-bold text-purple-300" data-testid="shared-horoscope-title">Horoscopo de {c.sign}</h2>
              {c.week && <p className="text-[var(--text-muted)] text-sm">{c.week}</p>}
            </div>
            <MarkdownRenderer content={c.horoscope} className="text-[var(--text-secondary)] text-sm" />
          </motion.div>
        )}
        {shareType === 'compatibility' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-purple-300" data-testid="shared-compat-title">Compatibilidad: {c.sign1} y {c.sign2}</h2>
              {c.relationship && <p className="text-[var(--text-muted)] text-sm">Relacion: {c.relationship}</p>}
            </div>
            <MarkdownRenderer content={c.analysis} className="text-[var(--text-secondary)] text-sm" />
          </motion.div>
        )}
        {shareType === 'natal_chart' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-purple-300" data-testid="shared-natal-title">Carta Natal: {c.sun_sign}</h2>
              {c.chart_type && <p className="text-purple-400 text-xs uppercase mb-1">{c.chart_type}</p>}
              {c.birth_date && <p className="text-[var(--text-muted)] text-sm">{c.birth_date}</p>}
            </div>
            {/* Gráfico de Carta Natal */}
            {c.chart_data && (
              <div className="flex flex-col items-center mb-6">
                <h4 className="text-lg font-semibold text-purple-300 mb-4">Rueda Zodiacal</h4>
                <NatalChartWheel chartData={c.chart_data} aspects={c.aspects || []} />
              </div>
            )}
            {c.planets?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-purple-300 mb-3">Posiciones Planetarias</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {c.planets.map((p, i) => (
                    <div key={i} className="flex justify-between items-center bg-black/30 rounded-lg px-3 py-2 border border-gold-300/8 min-w-0 overflow-hidden gap-2">
                      <span className="text-[var(--text-secondary)] text-xs sm:text-sm font-medium truncate shrink-0">{p.name}</span>
                      <div className="flex items-center gap-1 sm:gap-2 min-w-0">
                        <span className="text-white font-medium text-xs sm:text-sm truncate">{p.sign}</span>
                        {p.position && <span className="text-purple-400 text-[10px] sm:text-xs shrink-0">{p.position}°</span>}
                        {p.retrograde && <span className="text-red-400 text-[10px] sm:text-xs font-bold shrink-0">R</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {c.houses?.length > 0 && (
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-purple-300 mb-3">Casas Astrológicas</h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                  {c.houses.map((h, i) => (
                    <div key={i} className="bg-black/30 rounded-lg px-2 py-2 border border-gold-300/8 text-center overflow-hidden">
                      <span className="text-purple-400 text-[10px] sm:text-xs block">Casa {h.number}</span>
                      <span className="text-white font-medium text-xs sm:text-sm truncate block">{h.sign}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {c.aspects && c.aspects.length > 0 && (
              <AspectsTable aspects={c.aspects} />
            )}
            {c.analysis && <MarkdownRenderer content={c.analysis} className="text-[var(--text-secondary)] text-sm" />}
          </motion.div>
        )}
        {shareType === 'synastry' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <div className="glass-card p-6 sm:p-8 rounded-xl border border-pink-400/15 text-center">
              <h2 className="text-2xl font-bold text-pink-300 mb-1" data-testid="shared-synastry-title">
                {c.person_a?.name} ♡ {c.person_b?.name}
              </h2>
              <p className="text-[var(--text-muted)] text-sm">{c.person_a?.sun_sign} × {c.person_b?.sun_sign}</p>
            </div>
            {c.person_a?.chart_data && c.person_b?.chart_data && (
              <div className="glass-card p-6 rounded-xl border border-gold-300/10">
                <h3 className="text-lg font-bold text-purple-300 mb-4 text-center">Rueda de Sinastría</h3>
                <SynastryWheel chartDataA={c.person_a.chart_data} chartDataB={c.person_b.chart_data} crossAspects={c.cross_aspects || []} nameA={c.person_a.name} nameB={c.person_b.name} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {[['person_a','amber'],['person_b','cyan']].map(([key, color]) => {
                const person = c[key];
                return person?.planets?.length > 0 && (
                  <div key={key} className={`glass-card p-5 rounded-xl border ${color === 'amber' ? 'border-amber-400/10' : 'border-cyan-400/10'}`}>
                    <h4 className={`text-base font-bold mb-3 ${color === 'amber' ? 'text-amber-400' : 'text-cyan-400'}`}>{person.name} — {person.sun_sign}</h4>
                    <div className="space-y-1">
                      {person.planets.map((p, i) => (
                        <div key={i} className="flex justify-between items-center text-xs py-1 border-b border-white/5 last:border-0">
                          <span className="text-[var(--text-secondary)]">{p.name_es || p.name}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{p.sign}</span>
                            <span className="text-purple-400 text-[10px]">{p.position}°</span>
                            {p.retrograde && <span className="text-red-400 text-[10px] font-bold">R</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            {c.cross_aspects?.length > 0 && (
              <div className="glass-card p-6 rounded-xl border border-gold-300/10">
                <AspectsTable aspects={c.cross_aspects} />
              </div>
            )}
            {c.analysis && (
              <div className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
                <MarkdownRenderer content={c.analysis} className="text-[var(--text-secondary)] text-sm" />
              </div>
            )}
          </motion.div>
        )}
        {shareType === 'numerology' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <div className="text-center mb-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-amber-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-amber-500/30">
                <span className="text-4xl font-bold text-gray-900">{c.life_path}</span>
              </div>
              <h2 className="text-xl font-bold text-purple-300" data-testid="shared-numerology-title">Numero de Camino de Vida</h2>
              {c.name_number && <p className="text-[var(--text-muted)] text-sm">Numero del Nombre: {c.name_number}</p>}
            </div>
            {c.interpretation && <MarkdownRenderer content={c.interpretation} className="text-[var(--text-secondary)] text-sm" />}
          </motion.div>
        )}
        {shareType === 'iching' && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 sm:p-8 rounded-xl border border-gold-300/10">
            <div className="text-center mb-6">
              <p className="text-6xl mb-2">{c.hexagram_character}</p>
              <h2 className="text-xl font-bold text-purple-300" data-testid="shared-iching-title">I Ching: {c.hexagram_name}</h2>
              <p className="text-[var(--text-muted)] text-sm">Hexagrama #{c.hexagram_number}</p>
              {c.reading_type && <span className="inline-block bg-purple-500/20 text-purple-300 px-3 py-0.5 rounded-full text-xs mt-2">{c.reading_type}</span>}
              {c.question && <p className="text-[var(--text-muted)] text-sm mt-2 italic">"{c.question}"</p>}
              {c.has_mutations && c.mutated_name && (
                <p className="text-gold-300 text-sm mt-3">Mutacion: {c.mutated_name} {c.mutated_character}</p>
              )}
            </div>
            {c.interpretation && <MarkdownRenderer content={c.interpretation} className="text-[var(--text-secondary)] text-sm" />}
          </motion.div>
        )}
        <div className="text-center mt-8">
          <p className="text-[var(--text-muted)] text-sm mb-4">Descubre tu propio destino con Madame Blavatsky</p>
          <Link to="/" className="btn-gold inline-block" data-testid="shared-cta">Obtener Mi Lectura Gratuita</Link>
        </div>
      </div>
    </div>
  );
}
